import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Leaf, Waves, Wind, Sprout, Award, TrendingDown } from "lucide-react";
import { motion } from "framer-motion";
import ImpactFeed from "@/components/ImpactFeed";

const stats = [
  { label: "Tons CO₂ offset", value: "2.4M" },
  { label: "Verified projects", value: "180+" },
  { label: "Corporate clients", value: "540" },
  { label: "Countries active", value: "42" },
];

const categories = [
  { icon: Leaf, label: "Forestry", color: "bg-[#2C5530]/10 text-[#2C5530]" },
  { icon: Wind, label: "Renewable", color: "bg-sky-100 text-sky-800" },
  { icon: Waves, label: "Blue Carbon", color: "bg-teal-100 text-teal-800" },
  { icon: Sprout, label: "Agriculture", color: "bg-amber-100 text-amber-800" },
];

export default function Landing() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 bento-hero">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 flex flex-col justify-center"
          >
            <Badge className="w-fit bg-[#2C5530]/10 text-[#2C5530] hover:bg-[#2C5530]/10 border-0 rounded-full px-4 py-1.5" data-testid="hero-badge">
              Verified · Verra · Gold Standard
            </Badge>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light leading-[1.02]">
              Turn emissions into <span className="leaf-underline text-[#2C5530] italic font-normal">restoration</span>.
            </h1>
            <p className="mt-6 text-lg text-stone-600 max-w-xl leading-relaxed">
              CarbonVaults connects polluting industries in <span className="text-[#2C5530] font-medium">every country</span> with vetted carbon offset projects — forests, oceans, renewables, direct-air capture. Pay in your currency or with crypto, retire real credits in minutes.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/marketplace">
                <Button data-testid="hero-browse-btn" className="bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full px-7 h-12 text-base">
                  Browse credits <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/ai-assistant">
                <Button data-testid="hero-ai-btn" variant="outline" className="rounded-full px-7 h-12 text-base border-stone-300 hover:bg-stone-50">
                  Estimate my footprint
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-4 gap-6 max-w-lg">
              {stats.map((s) => (
                <div key={s.label} data-testid={`stat-${s.label}`}>
                  <div className="text-2xl sm:text-3xl font-light tracking-tight text-[#1C1917]">{s.value}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-stone-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Bento image side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-6 grid grid-cols-6 grid-rows-6 gap-4 min-h-[520px]"
          >
            <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden relative">
              <img src="https://images.unsplash.com/photo-1440581572325-0bea30075d9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxmb3Jlc3QlMjBjYXJib24lMjBvZmZzZXQlMjBwcm9qZWN0fGVufDB8fHx8MTc4NDYyMjMwOXww&ixlib=rb-4.1.0&q=85" alt="Rainforest" className="w-full h-full object-cover"/>
              <div className="noise-overlay"></div>
              <div className="absolute bottom-4 left-4 bg-white/85 backdrop-blur px-3 py-2 rounded-xl">
                <div className="text-xs uppercase tracking-widest text-stone-500">Featured</div>
                <div className="text-sm font-medium">Amazon Preservation · $18.50/ton</div>
              </div>
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden">
              <img src="https://images.pexels.com/photos/14468163/pexels-photo-14468163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Wind" className="w-full h-full object-cover"/>
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1708649290066-5f617003b93f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxvY2VhbiUyMGNvbnNlcnZhdGlvbiUyMGNvcmFsJTIwcmVlZnxlbnwwfHx8fDE3ODQ2MjIzMTB8MA&ixlib=rb-4.1.0&q=85" alt="Ocean" className="w-full h-full object-cover"/>
            </div>
            <div className="col-span-4 row-span-2 rounded-3xl bg-[#2C5530] text-white p-6 flex flex-col justify-between">
              <TrendingDown className="w-6 h-6 text-[#D97757]"/>
              <div>
                <div className="text-3xl font-light tracking-tight">-100%</div>
                <div className="text-sm text-white/70 mt-1">Net-zero pathways for teams of any size</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-t border-stone-200/80 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">What you can buy</div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mt-2">Every credit is a story of real climate work</h2>
            </div>
            <Link to="/marketplace" className="hidden sm:inline-flex text-sm text-[#2C5530] items-center gap-1 hover:gap-2 transition-all">
              See all projects <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="p-6 border border-stone-200 rounded-2xl hover:-translate-y-1 hover:shadow-lg transition-transform duration-300 bg-white">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.color}`}>
                    <Icon className="w-5 h-5"/>
                  </div>
                  <div className="mt-4 font-medium">{c.label}</div>
                  <div className="text-sm text-stone-500 mt-1">Discover verified {c.label.toLowerCase()} projects across the globe.</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <ImpactFeed />

      <section className="bg-[#FAFAF9] border-t border-stone-200/80">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {[
            {n:"01", t:"Measure", d:"Use our AI advisor to estimate your annual emissions from a handful of numbers."},
            {n:"02", t:"Match", d:"Get 3 recommended projects matched to your industry, budget, and impact goals."},
            {n:"03", t:"Retire", d:"Buy credits by the ton, receive a permanent digital certificate, share your progress."},
          ].map((s) => (
            <div key={s.n} className="p-8 bg-white border border-stone-200 rounded-2xl">
              <div className="text-6xl font-light tracking-tighter text-[#D97757]">{s.n}</div>
              <div className="mt-4 text-xl font-medium">{s.t}</div>
              <div className="mt-2 text-stone-600">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications strip */}
      <section className="bg-white border-t border-stone-200/80">
        <div className="max-w-7xl mx-auto px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Award className="w-6 h-6 text-[#2C5530]"/>
            <div>
              <div className="font-medium">Every credit is triple-verified</div>
              <div className="text-sm text-stone-500">Verra · Gold Standard · Plan Vivo · Puro.earth</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/enterprise">
              <Button data-testid="enterprise-cta" variant="outline" className="rounded-full h-11 px-6 border-stone-300">Enterprise · 10,000+ tons</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="footer-cta" className="bg-[#D97757] hover:bg-[#c26343] text-white rounded-full px-7 h-11">Create a company account</Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200/80 py-8 text-center text-sm text-stone-500">
        © 2026 Carbon Vaults · Building a regenerative economy
      </footer>
    </div>
  );
}
